<script setup lang="ts">
/*
 * Root component that renders the active toast stack. Mount once in App.vue
 * (or the layout). Pushes are made via `useToast().push(...)`.
 *
 * Visual: pill-shaped, bottom-center, dark surface for plain `info`; tinted
 * for success/warning/error to match the design tokens.
 */
import { useToast } from '@/composables/useToast';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-vue-next';

const { items, dismiss } = useToast();

const variantClasses = {
  info: 'bg-app-fg text-white',
  success: 'bg-emerald-700 text-white',
  warning: 'bg-amber-100 text-amber-800 border border-amber-200',
  error: 'bg-red-700 text-white',
} as const;

const variantIcon = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
} as const;
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 bottom-6 z-[200] flex flex-col items-center gap-2"
    role="region"
    aria-live="polite"
    aria-label="Notifications"
  >
    <transition-group
      name="toast"
      tag="div"
      class="flex flex-col items-center gap-2"
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-2 opacity-0"
    >
      <div
        v-for="t in items"
        :key="t.id"
        :class="[
          'pointer-events-auto inline-flex max-w-md items-center gap-2 rounded-pill px-4 py-2 text-md shadow-toast',
          variantClasses[t.variant],
        ]"
        role="status"
      >
        <component :is="variantIcon[t.variant]" class="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{{ t.message }}</span>
        <button
          type="button"
          class="-mr-1 ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-current/80 hover:text-current"
          aria-label="Dismiss"
          @click="dismiss(t.id)"
        >
          <X class="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </transition-group>
  </div>
</template>
